import { z } from "zod";

import type { EmployeeAllergyDto } from "@/generated/model/employeeAllergyDto";
import type { EmployeeContactDetailsDto } from "@/generated/model/employeeContactDetailsDto";
import type { EmployeeEmergencyContactDto } from "@/generated/model/employeeEmergencyContactDto";
import type { EmployeeHealthInformationDto } from "@/generated/model/employeeHealthInformationDto";
import type { EmployeeMedicationDto } from "@/generated/model/employeeMedicationDto";
import type { EmployeePersonalInformationDto } from "@/generated/model/employeePersonalInformationDto";
import { FitToWorkStatus } from "@/generated/model/fitToWorkStatus";
import type { UpdateEmployeeAdditionalNotesRequestDto } from "@/generated/model/updateEmployeeAdditionalNotesRequestDto";
import type { UpdateEmployeeAllergiesRequestDto } from "@/generated/model/updateEmployeeAllergiesRequestDto";
import type { UpdateEmployeeContactDetailsRequestDto } from "@/generated/model/updateEmployeeContactDetailsRequestDto";
import type { UpdateEmployeeEmergencyContactRequestDto } from "@/generated/model/updateEmployeeEmergencyContactRequestDto";
import type { UpdateEmployeeHealthInformationRequestDto } from "@/generated/model/updateEmployeeHealthInformationRequestDto";
import type { UpdateEmployeeMedicationRequestDto } from "@/generated/model/updateEmployeeMedicationRequestDto";
import type { UpdateEmployeePersonalInformationRequestDto } from "@/generated/model/updateEmployeePersonalInformationRequestDto";
import { blankToNull } from "@/lib/people";

// The same rules the server applies, so most mistakes show before saving.
// Every form holds text as a string: "" means not recorded.

const NI_NUMBER =
  /^(?!BG|GB|KN|NK|NT|TN|ZZ)[A-CEGHJ-PR-TW-Z][A-CEGHJ-NPR-TW-Z]\d{6}[A-D]$/;
const PHONE_NUMBER = /^(?=(?:\D*\d){7,15}\D*$)\+?[\d\s()-]+$/;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
export const MAX_ALLERGIES = 50;

/**
 * What a date field holds while its input is part-typed. The browser reports
 * such an input as "", which would otherwise save as "not recorded" and wipe
 * the stored date.
 */
export const INCOMPLETE_DATE = "incomplete";

/** A yes/no question as a radio group holds it: "" means not answered. */
export type YesNoValue = "yes" | "no" | "";

export function toYesNoValue(value: boolean | null): YesNoValue {
  if (value === null) return "";
  return value ? "yes" : "no";
}

function fromYesNoValue(value: YesNoValue): boolean | null {
  if (value === "") return null;
  return value === "yes";
}

const text = (label: string, max: number) =>
  z.string().max(max, `${label} must be ${max} characters or fewer`);

const phone = (label: string) =>
  text(label, 30).refine(
    (value) => value.trim() === "" || PHONE_NUMBER.test(value.trim()),
    "Enter a phone number, using digits, spaces and + ( ) -",
  );

const email = (label: string) =>
  text(label, 254).refine(
    (value) => value.trim() === "" || z.email().safeParse(value.trim()).success,
    "Enter an email address, like name@example.com",
  );

const dateOnly = (label: string) =>
  z
    .string()
    .refine(
      (value) => value === "" || isCalendarDate(value),
      `Enter a full ${label}, or clear it`,
    );

function isCalendarDate(value: string): boolean {
  if (!DATE_ONLY.test(value)) return false;
  const date = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().startsWith(value);
}

const yesNoValue = z.enum(["yes", "no", ""]);

export interface PersonalInformationFormValues {
  dateOfBirth: string;
  nationality: string;
  nationalInsuranceNumber: string;
  placeOfBirth: string;
  preferredLanguage: string;
  drivingLicenceHolder: YesNoValue;
  dbsCheckEnforcementDate: string;
}

export const personalInformationSchema = z.object({
  dateOfBirth: dateOnly("date of birth"),
  nationality: text("Nationality", 100),
  nationalInsuranceNumber: z
    .string()
    .refine(
      (value) =>
        value.trim() === "" ||
        NI_NUMBER.test(value.replace(/\s+/g, "").toUpperCase()),
      "Enter a National Insurance number in the format AB 12 34 56 C",
    ),
  placeOfBirth: text("Place of birth", 100),
  preferredLanguage: text("Preferred language", 100),
  drivingLicenceHolder: yesNoValue,
  dbsCheckEnforcementDate: dateOnly("DBS check enforcement date"),
});

export function personalInformationFormValues(
  dto: EmployeePersonalInformationDto,
): PersonalInformationFormValues {
  return {
    dateOfBirth: dto.dateOfBirth ?? "",
    nationality: dto.nationality ?? "",
    nationalInsuranceNumber: dto.nationalInsuranceNumber ?? "",
    placeOfBirth: dto.placeOfBirth ?? "",
    preferredLanguage: dto.preferredLanguage ?? "",
    drivingLicenceHolder: toYesNoValue(dto.drivingLicenceHolder),
    dbsCheckEnforcementDate: dto.dbsCheckEnforcementDate ?? "",
  };
}

export function personalInformationRequest(
  values: PersonalInformationFormValues,
): UpdateEmployeePersonalInformationRequestDto {
  return {
    dateOfBirth: blankToNull(values.dateOfBirth),
    nationality: blankToNull(values.nationality),
    nationalInsuranceNumber: blankToNull(values.nationalInsuranceNumber),
    placeOfBirth: blankToNull(values.placeOfBirth),
    preferredLanguage: blankToNull(values.preferredLanguage),
    drivingLicenceHolder: fromYesNoValue(values.drivingLicenceHolder),
    dbsCheckEnforcementDate: blankToNull(values.dbsCheckEnforcementDate),
  };
}

/**
 * The per-field reasons in a 400 from the server's validation filter
 * (`{ details: [{ field, errors }] }`), keyed like the form's own errors.
 */
export function serverFieldErrors(error: unknown): Record<string, string> {
  const details = (error as { response?: { data?: { details?: unknown } } })
    ?.response?.data?.details;
  if (!Array.isArray(details)) return {};
  const fieldErrors: Record<string, string> = {};
  for (const detail of details as { field?: unknown; errors?: unknown }[]) {
    const message = Array.isArray(detail?.errors) ? detail.errors[0] : null;
    if (typeof detail?.field === "string" && typeof message === "string") {
      fieldErrors[detail.field] ??= message;
    }
  }
  return fieldErrors;
}

export interface ContactDetailsFormValues {
  mobile: string;
  secondaryMobile: string;
  homePhone: string;
  personalEmail: string;
  workEmail: string;
}

export const contactDetailsSchema = z.object({
  mobile: phone("Mobile"),
  secondaryMobile: phone("Secondary mobile"),
  homePhone: phone("Home phone"),
  personalEmail: email("Personal email"),
  workEmail: email("Work email"),
});

export function contactDetailsFormValues(
  dto: EmployeeContactDetailsDto,
): ContactDetailsFormValues {
  return {
    mobile: dto.mobile ?? "",
    secondaryMobile: dto.secondaryMobile ?? "",
    homePhone: dto.homePhone ?? "",
    personalEmail: dto.personalEmail ?? "",
    workEmail: dto.workEmail ?? "",
  };
}

export function contactDetailsRequest(
  values: ContactDetailsFormValues,
): UpdateEmployeeContactDetailsRequestDto {
  return {
    mobile: blankToNull(values.mobile),
    secondaryMobile: blankToNull(values.secondaryMobile),
    homePhone: blankToNull(values.homePhone),
    personalEmail: blankToNull(values.personalEmail),
    workEmail: blankToNull(values.workEmail),
  };
}

export interface EmergencyContactFormValues {
  name: string;
  relationship: string;
  mobile: string;
  alternativeNumber: string;
  homeAddress: string;
}

export const emergencyContactSchema = z.object({
  name: text("Name", 200),
  relationship: text("Relationship", 100),
  mobile: phone("Mobile"),
  alternativeNumber: phone("Alternative number"),
  homeAddress: text("Home address", 500),
});

export function emergencyContactFormValues(
  dto: EmployeeEmergencyContactDto,
): EmergencyContactFormValues {
  return {
    name: dto.name ?? "",
    relationship: dto.relationship ?? "",
    mobile: dto.mobile ?? "",
    alternativeNumber: dto.alternativeNumber ?? "",
    homeAddress: dto.homeAddress ?? "",
  };
}

export function emergencyContactRequest(
  values: EmergencyContactFormValues,
): UpdateEmployeeEmergencyContactRequestDto {
  return {
    name: blankToNull(values.name),
    relationship: blankToNull(values.relationship),
    mobile: blankToNull(values.mobile),
    alternativeNumber: blankToNull(values.alternativeNumber),
    homeAddress: blankToNull(values.homeAddress),
  };
}

export interface AdditionalNotesFormValues {
  additionalNotes: string;
}

export const additionalNotesSchema = z.object({
  additionalNotes: text("Notes", 5000),
});

export function additionalNotesFormValues(
  additionalNotes: string | null,
): AdditionalNotesFormValues {
  return { additionalNotes: additionalNotes ?? "" };
}

export function additionalNotesRequest(
  values: AdditionalNotesFormValues,
): UpdateEmployeeAdditionalNotesRequestDto {
  return { additionalNotes: blankToNull(values.additionalNotes) };
}

/** `key` only keeps React's list stable while rows are added and removed; it is never sent. */
export interface AllergyFormRow {
  key: string;
  allergen: string;
  severityNotes: string;
}

export interface AllergiesFormValues {
  allergies: AllergyFormRow[];
}

const allergyRowSchema = z
  .object({
    key: z.string(),
    allergen: text("Allergen", 100),
    severityNotes: text("Severity notes", 1000),
  })
  .refine(
    (row) => row.allergen.trim() !== "" || row.severityNotes.trim() === "",
    { message: "Enter the allergen", path: ["allergen"] },
  );

export const allergiesSchema = z.object({
  allergies: z
    .array(allergyRowSchema)
    .refine(
      (rows) => rows.filter((row) => !isBlankRow(row)).length <= MAX_ALLERGIES,
      `Record at most ${MAX_ALLERGIES} allergies`,
    ),
});

// A counter, not crypto.randomUUID: that is missing outside a secure context.
let nextRowKey = 0;

function rowKey(): string {
  nextRowKey += 1;
  return `allergy-row-${nextRowKey}`;
}

export function newAllergyRow(): AllergyFormRow {
  return { key: rowKey(), allergen: "", severityNotes: "" };
}

export function allergiesFormValues(
  allergies: EmployeeAllergyDto[],
): AllergiesFormValues {
  return {
    allergies: allergies.map((allergy) => ({
      key: rowKey(),
      allergen: allergy.allergen,
      severityNotes: allergy.severityNotes ?? "",
    })),
  };
}

/** The whole list, in order. A row left completely blank is dropped. */
export function allergiesRequest(
  values: AllergiesFormValues,
): UpdateEmployeeAllergiesRequestDto {
  return {
    allergies: values.allergies
      .filter((row) => !isBlankRow(row))
      .map((row) => ({
        allergen: row.allergen.trim(),
        severityNotes: blankToNull(row.severityNotes),
      })),
  };
}

function isBlankRow(row: AllergyFormRow): boolean {
  return row.allergen.trim() === "" && row.severityNotes.trim() === "";
}

export interface HealthInformationFormValues {
  significantMedicalConditions: string;
  additionalInformation: string;
  reasonableAdjustments: string;
  fitToWorkStatus: FitToWorkStatus | "";
}

export const healthInformationSchema = z.object({
  significantMedicalConditions: text("Significant medical conditions", 2000),
  additionalInformation: text("Additional information", 2000),
  reasonableAdjustments: text("Reasonable adjustments", 2000),
  fitToWorkStatus: z.union([z.enum(FitToWorkStatus), z.literal("")]),
});

export function healthInformationFormValues(
  dto: EmployeeHealthInformationDto,
): HealthInformationFormValues {
  return {
    significantMedicalConditions: dto.significantMedicalConditions ?? "",
    additionalInformation: dto.additionalInformation ?? "",
    reasonableAdjustments: dto.reasonableAdjustments ?? "",
    fitToWorkStatus: dto.fitToWorkStatus ?? "",
  };
}

export function healthInformationRequest(
  values: HealthInformationFormValues,
): UpdateEmployeeHealthInformationRequestDto {
  return {
    significantMedicalConditions: blankToNull(
      values.significantMedicalConditions,
    ),
    additionalInformation: blankToNull(values.additionalInformation),
    reasonableAdjustments: blankToNull(values.reasonableAdjustments),
    fitToWorkStatus: values.fitToWorkStatus || null,
  };
}

export interface MedicationFormValues {
  takesRegularMedication: YesNoValue;
  regularMedicationDetails: string;
  emergencyMedicationDetails: string;
}

export const medicationSchema = z.object({
  takesRegularMedication: yesNoValue,
  regularMedicationDetails: text("Regular medication details", 2000),
  emergencyMedicationDetails: text("Emergency medication details", 2000),
});

export function medicationFormValues(
  dto: EmployeeMedicationDto,
): MedicationFormValues {
  return {
    takesRegularMedication: toYesNoValue(dto.takesRegularMedication),
    regularMedicationDetails: dto.regularMedicationDetails ?? "",
    emergencyMedicationDetails: dto.emergencyMedicationDetails ?? "",
  };
}

/**
 * The details field shows for a Yes, and whenever it holds text, so nothing is
 * saved (or cleared) out of sight.
 */
export function showsRegularMedicationDetails(
  values: MedicationFormValues,
): boolean {
  if (values.takesRegularMedication === "yes") return true;
  return values.regularMedicationDetails.trim() !== "";
}

export function medicationRequest(
  values: MedicationFormValues,
): UpdateEmployeeMedicationRequestDto {
  return {
    takesRegularMedication: fromYesNoValue(values.takesRegularMedication),
    regularMedicationDetails: blankToNull(values.regularMedicationDetails),
    emergencyMedicationDetails: blankToNull(values.emergencyMedicationDetails),
  };
}
